import React, { useState, useContext, useEffect } from "react";
import "../styles/PricinguiModern.css";
import {
  createOrder,
  verifyPayment,
  loadRazorpay,
  getActivePlansid,
  redeemPoints,
} from "../services/pricingService";
import { getPointsStatistics } from "../services/referalServices";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../Hook/AuthProvider";
import {
  Check,
  CheckCircle2,
  Sparkles,
  ArrowLeft,
  ShieldCheck,
  Coins,
  Lock,
  Zap,
} from "lucide-react";

const COMPARISON_FEATURES = [
  {
    category: "AI Mentor Queries",
    desc: "Personalized UPSC doubt solving & concepts",
    free: "5 queries / day",
    pro: "Unlimited priority access",
  },
  {
    category: "Mains Answer Evaluation",
    desc: "In-depth approach, score & model answers",
    free: "Limited evaluations",
    pro: "Unlimited evaluations",
  },
  {
    category: "Daily Current Affairs",
    desc: "Daily articles, editorials & analytical quiz",
    free: "Standard access",
    pro: "Full archive & deep analysis",
  },
  {
    category: "Study Mode & Syllabus",
    desc: "Interactive topic breakdown & high-yield summaries",
    free: "Limited topics",
    pro: "Full syllabus access",
  },
  {
    category: "Prelims & Mains Tests",
    desc: "Full-length mocks with performance insights",
    free: "Limited tests",
    pro: "Unlimited practice tests",
  },
  {
    category: "Cloud Notes & Storage",
    desc: "Save highlights, case studies & synced notes",
    free: "Up to 500 MB",
    pro: "Up to 2 GB",
  },
];

const Pricingui = () => {
  const { profileData, fetchProfile } = useContext(AuthContext);
  const navigate = useNavigate();

  // Mobile App detection
  const isFromApp = new URLSearchParams(window.location.search).get("source") === "app";

  // Plans & Selection state
  const [plansData, setPlansData] = useState([]);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Yooki Points state
  const [yookiBalance, setYookiBalance] = useState(0);
  const [pointsApplied, setPointsApplied] = useState(false);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);

  // Checkout & Modal state
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;
  const isPro = profileData?.user_type === "pro";

  // 1. Fetch available plans from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const data = await getActivePlansid();
        const proPlans = (data || []).filter((p) => p.plan_name?.includes("Pro"));
        setPlansData(proPlans);

        if (proPlans.length > 0) {
          // Default to the 12-month / yearly plan if available (usually index 2 or largest duration)
          const defaultIdx = proPlans.findIndex((p) => p.duration_months === 12);
          const initialIdx = defaultIdx !== -1 ? defaultIdx : proPlans.length - 1;
          setSelectedPlanIndex(initialIdx);
          setSelectedPlan(proPlans[initialIdx]);
        }
      } catch (err) {
        console.error("Failed to load plans:", err);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  // 2. Fetch user's Yooki points balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const stats = await getPointsStatistics();
        const pts = stats?.overall_statistics?.total_points || 0;
        setYookiBalance(pts);
      } catch (err) {
        console.warn("Failed to load Yooki points balance:", err);
      }
    };
    fetchBalance();
  }, []);

  // Select a plan cadence
  const handleSelectCadence = (index, plan) => {
    setSelectedPlanIndex(index);
    setSelectedPlan(plan);
    setPointsApplied(false);
    setPointsToRedeem(0);
    setPaymentError("");
  };

  // Toggle points redemption
  const handleTogglePoints = () => {
    if (pointsApplied) {
      setPointsApplied(false);
      setPointsToRedeem(0);
    } else {
      const basePrice = Math.round(Number(selectedPlan?.price_resolved || selectedPlan?.discounted_amount || selectedPlan?.actual_amount || 0));
      const usable = Math.min(yookiBalance, basePrice);
      setPointsToRedeem(usable);
      setPointsApplied(true);
    }
  };

  // App redirect on payment success
  const redirectToAppPaymentSuccess = (paymentResponse) => {
    const successUrl = new URL("/payment-success", window.location.origin);
    successUrl.searchParams.set("source", "pricing");
    successUrl.searchParams.set("status", "success");
    if (paymentResponse?.razorpay_payment_id) {
      successUrl.searchParams.set("payment_id", paymentResponse.razorpay_payment_id);
    }
    if (paymentResponse?.razorpay_order_id) {
      successUrl.searchParams.set("order_id", paymentResponse.razorpay_order_id);
    }
    window.location.replace(successUrl.toString());
  };

  // Calculate prices
  const basePrice = selectedPlan
    ? Math.round(Number(selectedPlan.price_resolved || selectedPlan.discounted_amount || selectedPlan.actual_amount))
    : 0;
  const actualPrice = selectedPlan ? Math.round(Number(selectedPlan.actual_amount)) : 0;
  const discountAmount = pointsApplied ? pointsToRedeem : 0;
  const finalPayable = Math.max(0, basePrice - discountAmount);
  const monthlyRate = selectedPlan?.duration_months
    ? Math.round(finalPayable / selectedPlan.duration_months)
    : finalPayable;

  // Execute Points Redemption helper
  const executeRedemption = async (planId, overrideId, basePrice, pointsUsed, finalPrice) => {
    const payload = {
      plan_id: String(planId),
      override_id: overrideId ? String(overrideId) : null,
      price_resolved: basePrice,
      points_used: pointsUsed,
      final_price: finalPrice,
      currency: "INR",
    };
    return await redeemPoints(payload);
  };

  // 3. Razorpay Payment Trigger
  const handlePayment = async () => {
    if (!selectedPlan) return;
    setPaymentError("");
    setIsProcessing(true);

    try {
      // 1. Create order on backend
      const order = await createOrder(
        finalPayable,
        selectedPlan.plan_id,
        selectedPlan.duration_months,
        null
      );

      // 2. Redeem points if applied
      if (pointsApplied && pointsToRedeem > 0) {
        try {
          await executeRedemption(
            selectedPlan.plan_id,
            selectedPlan.override_id,
            basePrice,
            pointsToRedeem,
            finalPayable
          );
        } catch (redeemErr) {
          console.error("Redemption failed:", redeemErr);
          setPaymentError("Could not apply reward points. Please retry.");
          setIsProcessing(false);
          return;
        }
      }

      // 3. Open Razorpay Checkout Modal
      const options = {
        key: RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Yooki Pro",
        description: `Upgrade to Yooki Pro (${selectedPlan.duration_months} ${selectedPlan.duration_months > 1 ? "months" : "month"})`,
        handler: async function (response) {
          setIsProcessing(true);
          try {
            const verifyRes = await verifyPayment({
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature,
              subscription_months: selectedPlan.duration_months,
            });

            if (verifyRes?.status === "success" || verifyRes?.valid) {
              if (fetchProfile) await fetchProfile();
              if (isFromApp) {
                redirectToAppPaymentSuccess(response);
              } else {
                setShowSuccessModal(true);
              }
            } else {
              setPaymentError(verifyRes?.detail || verifyRes?.message || "Payment verification failed.");
            }
          } catch (err) {
            console.error("Verification error:", err);
            setPaymentError("Payment verification failed. Please contact support.");
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          email: profileData?.email || "",
          contact: profileData?.phone_number || "",
        },
        theme: {
          color: "#0284c7",
          hide_topbar: false,
        },
      };

      await loadRazorpay();
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        setPaymentError(response?.error?.description || "Payment was cancelled or declined.");
        setIsProcessing(false);
      });
      rzp.open();
    } catch (err) {
      console.error("Order creation failed:", err);
      setPaymentError("Failed to initiate payment. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="modern-pricing-page">
      {/* Top Navigation */}
      <div className="modern-pricing-nav">
        <button
          type="button"
          className="modern-pricing-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          <ArrowLeft size={16} />
          <span>Back</span>
        </button>

        {yookiBalance > 0 && (
          <div className="modern-pricing-points-chip">
            <Coins size={16} />
            <span>
              Yooki Rewards: <strong>{yookiBalance} pts</strong>
            </span>
          </div>
        )}
      </div>

      {/* Hero Header */}
      <div className="modern-pricing-hero">
        <div className="modern-pricing-badge">
          <Sparkles size={13} />
          <span>Invest in your rank</span>
        </div>
        <h1 className="modern-pricing-title">
          Flexible Plans for Ambitious Aspirants
        </h1>
        <p className="modern-pricing-subtitle">
          Unlock unlimited AI mentoring, deep evaluation, and test analytics to accelerate your preparation.
        </p>
      </div>

      {/* Cadence Switcher (Pill) */}
      {!loadingPlans && plansData.length > 0 && (
        <div className="modern-cadence-wrapper">
          <div className="modern-cadence-pill">
            {plansData.map((plan, idx) => {
              const isYearly = plan.duration_months === 12;
              const isActive = selectedPlanIndex === idx;
              return (
                <button
                  key={plan.plan_id || idx}
                  type="button"
                  className={`modern-cadence-btn ${isActive ? "active" : ""}`}
                  onClick={() => handleSelectCadence(idx, plan)}
                >
                  <span>
                    {plan.duration_months === 1
                      ? "Monthly"
                      : plan.duration_months === 3
                        ? "Quarterly"
                        : `${plan.duration_months} Months`}
                  </span>
                  {isYearly && <span className="modern-save-chip">Save 35%</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Error Toast */}
      {paymentError && (
        <div
          style={{
            maxWidth: 600,
            width: "100%",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#dc2626",
            padding: "10px 16px",
            borderRadius: 8,
            marginBottom: 20,
            fontSize: "13.5px",
            textAlign: "center",
          }}
        >
          {paymentError}
        </div>
      )}

      {/* Pricing Cards Grid */}
      <div className="modern-cards-grid">
        {/* Free Plan Card */}
        <div className="modern-card modern-card-free">
          <div className="modern-card-header">
            <div className="modern-plan-name-row">
              <h3 className="modern-plan-name">Free Tier</h3>
            </div>
            <p className="modern-plan-desc">
              Essential daily tools to start your UPSC journey.
            </p>
          </div>

          <div className="modern-card-pricing">
            <span className="modern-price-large">₹0</span>
            <span className="modern-price-cadence">/ forever</span>
            <div className="modern-per-month-hint" style={{ color: "#64748b" }}>
              Standard access included
            </div>
          </div>

          <ul className="modern-feature-list">
            <li className="modern-feature-item">
              <CheckCircle2 size={16} className="modern-feat-icon-check" />
              <span>5 AI Mentor Queries per day</span>
            </li>
            <li className="modern-feature-item">
              <CheckCircle2 size={16} className="modern-feat-icon-check" />
              <span>Daily Current Affairs headlines</span>
            </li>
            <li className="modern-feature-item">
              <Lock size={16} className="modern-feat-icon-limit" />
              <span>Limited Mains answer evaluations</span>
            </li>
            <li className="modern-feature-item">
              <Lock size={16} className="modern-feat-icon-limit" />
              <span>Basic study summaries</span>
            </li>
            <li className="modern-feature-item">
              <Lock size={16} className="modern-feat-icon-limit" />
              <span>500 MB cloud storage</span>
            </li>
          </ul>

          <div className="modern-card-footer">
            <div className="modern-card-btn-free">
              {!isPro ? "Current Plan" : "Standard Tier"}
            </div>
            <div className="modern-secure-badge-placeholder" aria-hidden="true" />
          </div>
        </div>

        {/* Pro Plan Card (Hero) */}
        <div className="modern-card modern-card-pro">
          <div className="modern-pro-popular-tag">
            <Sparkles size={12} />
            <span>Most Popular</span>
          </div>

          <div className="modern-card-header">
            <div className="modern-plan-name-row">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="modern-plan-name">Yooki</span>
                <img
                  src="/assets/images/pro-button-gradient-1.svg"
                  alt="Pro"
                  className="modern-pro-logo-img"
                />
              </div>
            </div>
            <p className="modern-plan-desc">
              Unrestricted access to the entire UPSC AI suite & evaluation tools.
            </p>
          </div>

          <div className="modern-card-pricing">
            <div style={{ display: "flex", alignItems: "baseline" }}>
              <span className="modern-price-large">₹{finalPayable.toLocaleString("en-IN")}</span>
              {actualPrice > finalPayable && (
                <span className="modern-price-strike">₹{actualPrice.toLocaleString("en-IN")}</span>
              )}
              <span className="modern-price-cadence">
                / {selectedPlan?.duration_months === 1 ? "month" : `${selectedPlan?.duration_months || 1} months`}
              </span>
            </div>
            {selectedPlan?.duration_months > 1 && (
              <div className="modern-per-month-hint">
                Equivalent to ₹{monthlyRate}/month
              </div>
            )}
          </div>

          {/* Points Redemption Module */}
          {yookiBalance > 0 && (
            <div className="modern-points-box">
              <div className="modern-points-header">
                <span className="modern-points-title">
                  <Coins size={15} />
                  <span>Redeem Yooki Rewards</span>
                </span>
                <span className="modern-points-avail">
                  Balance: {yookiBalance} pts
                </span>
              </div>
              <div className="modern-points-action-row">
                <span className="modern-points-discount-text">
                  {pointsApplied
                    ? `✓ ₹${discountAmount} discount applied`
                    : `Use up to ${Math.min(yookiBalance, basePrice)} pts (₹${Math.min(yookiBalance, basePrice)} OFF)`}
                </span>
                <button
                  type="button"
                  className={`modern-points-toggle-btn ${pointsApplied ? "applied" : ""}`}
                  onClick={handleTogglePoints}
                >
                  {pointsApplied ? "Remove" : "Apply Points"}
                </button>
              </div>
            </div>
          )}

          <ul className="modern-feature-list">
            <li className="modern-feature-item">
              <Zap size={16} className="modern-feat-icon-check" />
              <span><strong>Unlimited priority AI Mentor queries</strong></span>
            </li>
            <li className="modern-feature-item">
              <Check size={16} className="modern-feat-icon-check" />
              <span><strong>Unlimited Mains Answer Evaluations</strong> with model approach</span>
            </li>
            <li className="modern-feature-item">
              <Check size={16} className="modern-feat-icon-check" />
              <span>Comprehensive Editorial & Daily Current Affairs Quiz</span>
            </li>
            <li className="modern-feature-item">
              <Check size={16} className="modern-feat-icon-check" />
              <span>Full Study Mode & high-yield revision summaries</span>
            </li>
            <li className="modern-feature-item">
              <Check size={16} className="modern-feat-icon-check" />
              <span>Unlimited Tests Series with personalized weakness heatmaps</span>
            </li>
          </ul>

          <div className="modern-card-footer">
            <button
              type="button"
              className="modern-card-btn-pro"
              onClick={handlePayment}
              disabled={isProcessing || loadingPlans}
            >
              {isProcessing ? (
                <>
                  <span className="modern-spinner" />
                  <span>Processing...</span>
                </>
              ) : (
                <span>{isPro ? "Extend Pro Access" : "Upgrade to Pro"}</span>
              )}
            </button>

            <div className="modern-secure-badge">
              <ShieldCheck size={14} />
              <span>256-Bit SSL Encrypted · Instant Activation</span>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Comparison Section */}
      <div className="modern-compare-section">
        <h3 className="modern-compare-title">Detailed Feature Comparison</h3>
        <div className="modern-compare-card">
          <table className="modern-compare-table">
            <thead>
              <tr>
                <th>Feature Capability</th>
                <th>Free</th>
                <th>Yooki Pro</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_FEATURES.map((feat, i) => (
                <tr key={i}>
                  <td>
                    <div className="modern-feat-name-cell">{feat.category}</div>
                    <div className="modern-feat-desc-sub">{feat.desc}</div>
                  </td>
                  <td>
                    <span className="modern-pill-limited">{feat.free}</span>
                  </td>
                  <td>
                    <span className="modern-pill-unlimited">
                      <Check size={13} /> {feat.pro}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modern-overlay-blur">
          <div className="modern-modal-card">
            <div className="modern-modal-icon-wrap">
              <CheckCircle2 size={32} />
            </div>
            <h3 className="modern-modal-title">Welcome to Yooki Pro!</h3>
            <p className="modern-modal-desc">
              Your subscription has been activated successfully. All Pro features, unlimited queries, advanced evaluations, and My Notes are now active.
            </p>
            <button
              type="button"
              className="modern-modal-btn"
              onClick={() => {
                setShowSuccessModal(false);
                navigate("/dashboard");
              }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricingui;
